/**
 * ProduktOverlayContent Component
 * Displays detailed Product Fit data in the overlay
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, TrendingUp, TrendingDown, Minus, FileText, Target, BarChart3, ExternalLink, ThumbsUp, ThumbsDown, Crosshair, Eye, EyeOff } from 'lucide-react';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import { getFitIndicatorLabel, getFitIndicatorColor } from '@/types/produkt';
import type { ProduktFullData, ProduktFitZielgruppeOptimierung, ProduktFitMarktOptimierung } from '@/types/produkt';
import type { ChartConfig } from '@/types/api';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';

export interface ProduktOverlayContentProps {
  data: ProduktFullData | string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
}

// Color for optimization preview net (green from styleguide)
const OPTIMIZATION_PREVIEW_COLOR = '#22c55e';

/**
 * Get the appropriate icon for fit indicator
 */
function FitIndicatorIcon({ value, className }: { value: number; className?: string }) {
  if (value >= 4) {
    return <TrendingUp className={className} />;
  }
  if (value >= 3) {
    return <Minus className={className} />;
  }
  return <TrendingDown className={className} />;
}

/**
 * Overlay content for Product Fit with full details
 */
export function ProduktOverlayContent({
  data,
  getDescription,
  featureName,
}: ProduktOverlayContentProps) {
  const { t } = useTranslation();
  // State for tracking which optimization is being previewed
  const [selectedZielgruppeOptIndex, setSelectedZielgruppeOptIndex] = useState<number | null>(null);
  const [selectedMarktOptIndex, setSelectedMarktOptIndex] = useState<number | null>(null);

  // Handle legacy string data
  if (typeof data === 'string') {
    return (
      <div className="space-y-6">
        {/* Header with icon and title */}
        <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
          <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
            <Package className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">{t('results.cards.produkt.title')}</h3>
        </div>

        {/* Main content section */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  const { main } = data;

  // Get selected optimization data for Zielgruppe
  const selectedZielgruppeOpt: ProduktFitZielgruppeOptimierung | null =
    selectedZielgruppeOptIndex !== null && main.Produkt_Fit_Zielgruppe_Optimierung?.[selectedZielgruppeOptIndex]
      ? main.Produkt_Fit_Zielgruppe_Optimierung[selectedZielgruppeOptIndex]
      : null;

  // Create radar chart config for Zielgruppe Matrix
  const zielgruppeRadarConfig = useMemo((): ChartConfig | null => {
    const matrix = main.Produkt_Fit_Zielgruppe_Matrix;
    if (!matrix || matrix.length === 0) {
      return null;
    }

    const categories = matrix.map(item => item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name || '');
    const values = matrix.map(item => item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent || 0);

    const series: Array<{ name: string; data: number[] }> = [{
      name: 'Zielgruppe',
      data: values,
    }];

    const colors: string[] = ['#6366f1'];
    const opacities: number[] = [0.3];
    const dashArrays: number[] = [0];
    const markerColors: string[] = ['#6366f1'];

    // Add Produktmerkmale series matched by array index
    const merkmale = main.Produkt_Fit_Produktmerkmale;
    if (merkmale && merkmale.length > 0) {
      const merkmaleValues = categories.map((_, index) =>
        index < merkmale.length ? (merkmale[index].Produkt_Fit_Produktmerkmale_Merkmal_Prozent || 0) : 0
      );
      series.push({ name: 'Produktmerkmale', data: merkmaleValues });
      colors.push('#FC03B0');
      opacities.push(0.15);
      dashArrays.push(0);
      markerColors.push('#FC03B0');
    }

    // Build map for quick lookup of main values
    const zielgruppeMap = new Map<string, number>();
    for (const item of matrix) {
      zielgruppeMap.set(item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name || '', item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent || 0);
    }

    // Add optimization preview series if selected
    if (selectedZielgruppeOpt?.Produkt_Fit_Zielgruppe_Matrix) {
      const optMap = new Map<string, number>();
      for (const item of selectedZielgruppeOpt.Produkt_Fit_Zielgruppe_Matrix) {
        if (item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name != null) {
          optMap.set(item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Name, item.Produkt_Fit_Zielgruppe_Matrix_Merkmal_Prozent || 0);
        }
      }
      const optValues = categories.map(cat => optMap.has(cat) ? optMap.get(cat)! : (zielgruppeMap.get(cat) ?? 0));
      series.push({ name: 'Optimiert', data: optValues });
      colors.push(OPTIMIZATION_PREVIEW_COLOR);
      opacities.push(0.15);
      dashArrays.push(5);
      markerColors.push(OPTIMIZATION_PREVIEW_COLOR);
    }

    const hasMultipleSeries = series.length > 1;

    return {
      type: 'radar',
      series: series as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'radar',
          offsetY: -20,
          parentHeightOffset: 0,
        },
        grid: {
          padding: {
            top: -20,
            right: 0,
            bottom: -20,
            left: 0,
          },
        },
        xaxis: {
          categories: categories,
          labels: {
            show: true,
            style: {
              fontSize: '11px',
              colors: Array(categories.length).fill('#32373c'),
            },
          },
        },
        yaxis: {
          show: false,
          max: 100,
          tickAmount: 5,
        },
        colors: hasMultipleSeries ? colors : ['#6366f1'],
        fill: {
          opacity: hasMultipleSeries ? opacities : 0.3,
        },
        stroke: {
          width: 2,
          dashArray: hasMultipleSeries ? dashArrays : 0,
        },
        markers: {
          size: 4,
          colors: hasMultipleSeries ? markerColors : ['#6366f1'],
          strokeColors: '#fff',
          strokeWidth: 1,
        },
        plotOptions: {
          radar: {
            size: 100,
            polygons: {
              strokeColors: '#e0e0e0',
              strokeWidth: 1,
              connectorColors: '#e0e0e0',
              fill: {
                colors: ['#fafafa', '#fff'],
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
        legend: {
          show: hasMultipleSeries,
          position: 'bottom',
          fontSize: '11px',
          markers: {
            width: 10,
            height: 10,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Produkt_Fit_Zielgruppe_Matrix, main.Produkt_Fit_Produktmerkmale, selectedZielgruppeOpt]);

  // Get selected optimization data for Markt
  const selectedMarktOpt: ProduktFitMarktOptimierung | null =
    selectedMarktOptIndex !== null && main.Produkt_Fit_Markt_Optimierung?.[selectedMarktOptIndex]
      ? main.Produkt_Fit_Markt_Optimierung[selectedMarktOptIndex]
      : null;

  // Create radar chart config for Markt Matrix
  const marktRadarConfig = useMemo((): ChartConfig | null => {
    const matrix = main.Produkt_Fit_Markt_Matrix;
    if (!matrix || matrix.length === 0) {
      return null;
    }

    const categories = matrix.map(item => item.Produkt_Fit_Markt_Matrix_Merkmal_Name || '');
    const values = matrix.map(item => item.Produkt_Fit_Markt_Matrix_Merkmal_Prozent || 0);

    const series: Array<{ name: string; data: number[] }> = [{
      name: 'Vertriebsgebiet',
      data: values,
    }];

    const colors: string[] = ['#FF6803'];
    const opacities: number[] = [0.3];
    const dashArrays: number[] = [0];
    const markerColors: string[] = ['#FF6803'];

    // Add Produktmerkmale series matched by array index
    const merkmaleMarkt = main.Produkt_Fit_Produktmerkmale;
    if (merkmaleMarkt && merkmaleMarkt.length > 0) {
      const merkmaleValues = categories.map((_, index) =>
        index < merkmaleMarkt.length ? (merkmaleMarkt[index].Produkt_Fit_Produktmerkmale_Merkmal_Prozent || 0) : 0
      );
      series.push({ name: 'Produktmerkmale', data: merkmaleValues });
      colors.push('#FC03B0');
      opacities.push(0.15);
      dashArrays.push(0);
      markerColors.push('#FC03B0');
    }

    // Build map for quick lookup of main values
    const marktMap = new Map<string, number>();
    for (const item of matrix) {
      marktMap.set(item.Produkt_Fit_Markt_Matrix_Merkmal_Name || '', item.Produkt_Fit_Markt_Matrix_Merkmal_Prozent || 0);
    }

    // Add optimization preview series if selected
    if (selectedMarktOpt?.Produkt_Fit_Markt_Matrix) {
      const optMap = new Map<string, number>();
      for (const item of selectedMarktOpt.Produkt_Fit_Markt_Matrix) {
        if (item.Produkt_Fit_Markt_Matrix_Merkmal_Name != null) {
          optMap.set(item.Produkt_Fit_Markt_Matrix_Merkmal_Name, item.Produkt_Fit_Markt_Matrix_Merkmal_Prozent || 0);
        }
      }
      const optValues = categories.map(cat => optMap.has(cat) ? optMap.get(cat)! : (marktMap.get(cat) ?? 0));
      series.push({ name: 'Optimiert', data: optValues });
      colors.push(OPTIMIZATION_PREVIEW_COLOR);
      opacities.push(0.15);
      dashArrays.push(5);
      markerColors.push(OPTIMIZATION_PREVIEW_COLOR);
    }

    const hasMultipleSeries = series.length > 1;

    return {
      type: 'radar',
      series: series as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'radar',
          offsetY: -20,
          parentHeightOffset: 0,
        },
        grid: {
          padding: {
            top: -20,
            right: 0,
            bottom: -20,
            left: 0,
          },
        },
        xaxis: {
          categories: categories,
          labels: {
            show: true,
            style: {
              fontSize: '11px',
              colors: Array(categories.length).fill('#32373c'),
            },
          },
        },
        yaxis: {
          show: false,
          max: 100,
          tickAmount: 5,
        },
        colors: hasMultipleSeries ? colors : ['#FF6803'],
        fill: {
          opacity: hasMultipleSeries ? opacities : 0.3,
        },
        stroke: {
          width: 2,
          dashArray: hasMultipleSeries ? dashArrays : 0,
        },
        markers: {
          size: 4,
          colors: hasMultipleSeries ? markerColors : ['#FF6803'],
          strokeColors: '#fff',
          strokeWidth: 1,
        },
        plotOptions: {
          radar: {
            size: 100,
            polygons: {
              strokeColors: '#e0e0e0',
              strokeWidth: 1,
              connectorColors: '#e0e0e0',
              fill: {
                colors: ['#fafafa', '#fff'],
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => `${val}%`,
          },
        },
        legend: {
          show: hasMultipleSeries,
          position: 'bottom',
          fontSize: '11px',
          markers: {
            width: 10,
            height: 10,
          },
        },
      } as ChartConfig['options'],
    };
  }, [main.Produkt_Fit_Markt_Matrix, main.Produkt_Fit_Produktmerkmale, selectedMarktOpt]);

  const fitIndicator = main.Produkt_Fit_Indikator || 0;
  const fitLabel = getFitIndicatorLabel(fitIndicator);
  const fitColor = getFitIndicatorColor(fitIndicator);

  return (
    <div className="space-y-6">
      {/* Header with icon and title */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <Package className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">{t('results.cards.produkt.title')}</h3>
      </div>

      {/* Product Fit Indicator + Product info - Indicator first on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Fit Indicator section - shown first */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-1">{t('results.cards.produkt.overlay.productFitIndicator')}</p>
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${fitColor}20` }}
            >
              <span style={{ color: fitColor }}>
                <FitIndicatorIcon value={fitIndicator} className="h-6 w-6" />
              </span>
            </div>
            <div>
              <p
                className="text-xl font-bold leading-tight"
                style={{ color: fitColor }}
              >
                {fitLabel}
              </p>
              <p className="text-xs text-[var(--color-charcoal)]/60">
                {fitIndicator} {t('results.cards.produkt.overlay.outOf5')}
              </p>
            </div>
            {/* Visual scale */}
            <div className="ml-auto flex gap-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: level <= fitIndicator ? fitColor : '#e5e7eb',
                    color: level <= fitIndicator ? 'white' : '#9ca3af',
                  }}
                >
                  {level}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product info section - shown second */}
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-xs text-[var(--color-charcoal)]/60 mb-1">{t('results.cards.produkt.overlay.product')}</p>
          <p className="text-lg font-semibold text-[var(--color-charcoal)] mb-3">
            {main.Produkt_Fit_Produktname || '-'}
          </p>
          {main.Produkt_Fit_Produktlink && main.Produkt_Fit_Produktlink !== '[placeholder]' && (
            <a
              href={main.Produkt_Fit_Produktlink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[var(--color-primary-start)] hover:underline inline-flex items-center gap-1"
            >
              {t('results.cards.produkt.overlay.productLink')}
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>

      {/* Zielgruppen-Fit Section: Radar + Optimierungsvorschläge side by side */}
      <div className="pt-4 border-t border-[var(--glass-border)]">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-5 w-5 text-[var(--color-primary-start)]" />
          <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.targetGroupFit')}</h4>
          <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="targetGroupFit" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Zielgruppe Fit Radar */}
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <div className="-mx-2 -my-4">
              {zielgruppeRadarConfig ? (
                <AnalysisChart
                  config={zielgruppeRadarConfig}
                  height={280}
                />
              ) : (
                <div className="h-[280px] flex items-center justify-center mx-2 my-4">
                  <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.produkt.overlay.noDataAvailable')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Zielgruppe Optimierungsvorschläge */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-[var(--color-charcoal)]/70 mb-2">{t('results.cards.produkt.overlay.optimizationSuggestions')}</p>
            {main.Produkt_Fit_Zielgruppe_Optimierung?.length > 0 ? (
              main.Produkt_Fit_Zielgruppe_Optimierung.map((opt, index) => {
                const isSelected = selectedZielgruppeOptIndex === index;
                return (
                  <div
                    key={`zg-${index}`}
                    className={`p-3 rounded-[var(--radius-md)] border transition-all ${
                      isSelected
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/40'
                        : 'bg-gradient-to-r from-[var(--color-primary-start)]/5 to-transparent border-[var(--glass-border)]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[var(--color-primary-start)]/10 shrink-0">
                        <Target className="h-4 w-4 text-[var(--color-primary-start)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-[var(--color-charcoal)]">{opt.Name}</p>
                        <p className="text-xs text-[var(--color-charcoal)]/70 mt-0.5 line-clamp-2">{opt.Beschreibung}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedZielgruppeOptIndex(isSelected ? null : index)}
                        className={`p-1.5 rounded-lg shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-[#22c55e]/20 text-[#22c55e]'
                            : 'bg-[var(--glass-bg)] text-[var(--color-charcoal)]/50 hover:text-[var(--color-charcoal)] hover:bg-[var(--color-primary-start)]/10'
                        }`}
                        title={isSelected ? t('results.cards.produkt.overlay.hidePreview') : t('results.cards.produkt.overlay.showPreview')}
                      >
                        {isSelected ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-3 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.produkt.overlay.noSuggestionsAvailable')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Markt-Fit Section: Radar + Optimierungsvorschläge side by side */}
      <div className="pt-4 border-t border-[var(--glass-border)]">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-[var(--color-primary-end)]" />
          <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.marketFit')}</h4>
          <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="marketFit" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Markt Fit Radar */}
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <div className="-mx-2 -my-4">
              {marktRadarConfig ? (
                <AnalysisChart
                  config={marktRadarConfig}
                  height={280}
                />
              ) : (
                <div className="h-[280px] flex items-center justify-center mx-2 my-4">
                  <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.produkt.overlay.noDataAvailable')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Markt Optimierungsvorschläge */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-[var(--color-charcoal)]/70 mb-2">{t('results.cards.produkt.overlay.optimizationSuggestions')}</p>
            {main.Produkt_Fit_Markt_Optimierung?.length > 0 ? (
              main.Produkt_Fit_Markt_Optimierung.map((opt, index) => {
                const isSelected = selectedMarktOptIndex === index;
                return (
                  <div
                    key={`mk-${index}`}
                    className={`p-3 rounded-[var(--radius-md)] border transition-all ${
                      isSelected
                        ? 'bg-[#22c55e]/10 border-[#22c55e]/40'
                        : 'bg-gradient-to-r from-[var(--color-primary-end)]/5 to-transparent border-[var(--glass-border)]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[var(--color-primary-end)]/10 shrink-0">
                        <BarChart3 className="h-4 w-4 text-[var(--color-primary-end)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-[var(--color-charcoal)]">{opt.Name}</p>
                        <p className="text-xs text-[var(--color-charcoal)]/70 mt-0.5 line-clamp-2">{opt.Beschreibung}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedMarktOptIndex(isSelected ? null : index)}
                        className={`p-1.5 rounded-lg shrink-0 transition-colors ${
                          isSelected
                            ? 'bg-[#22c55e]/20 text-[#22c55e]'
                            : 'bg-[var(--glass-bg)] text-[var(--color-charcoal)]/50 hover:text-[var(--color-charcoal)] hover:bg-[var(--color-primary-end)]/10'
                        }`}
                        title={isSelected ? t('results.cards.produkt.overlay.hidePreview') : t('results.cards.produkt.overlay.showPreview')}
                      >
                        {isSelected ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-3 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
                <span className="text-sm text-[var(--color-charcoal)]/50">{t('results.cards.produkt.overlay.noSuggestionsAvailable')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Strengths and Weaknesses side by side - only show if there are non-empty items */}
      {(main.Produkt_Fit_Staerken?.filter(s => s.Name && s.Name !== '[placeholder]').length > 0 || main.Produkt_Fit_Schwaechen?.filter(s => s.Name && s.Name !== '[placeholder]').length > 0) && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Strengths */}
            {main.Produkt_Fit_Staerken?.filter(s => s.Name && s.Name !== '[placeholder]').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsUp className="h-5 w-5 text-green-500" />
                  <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.strengths')}</h4>
                  <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="strengths" />
                </div>
                <div className="space-y-3">
                  {main.Produkt_Fit_Staerken.filter(s => s.Name && s.Name !== '[placeholder]').map((staerke, index) => (
                    <div key={index} className="p-3 bg-green-50 rounded-[var(--radius-md)] border border-green-200">
                      <p className="font-semibold text-[var(--color-charcoal)] text-sm">{staerke.Name}</p>
                      {staerke.Beschreibung && staerke.Beschreibung !== '[placeholder]' && (
                        <p className="text-xs text-[var(--color-charcoal)]/70 mt-1">{staerke.Beschreibung}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weaknesses */}
            {main.Produkt_Fit_Schwaechen?.filter(s => s.Name && s.Name !== '[placeholder]').length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsDown className="h-5 w-5 text-red-500" />
                  <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.weaknesses')}</h4>
                  <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="weaknesses" />
                </div>
                <div className="space-y-3">
                  {main.Produkt_Fit_Schwaechen.filter(s => s.Name && s.Name !== '[placeholder]').map((schwaeche, index) => (
                    <div key={index} className="p-3 bg-red-50 rounded-[var(--radius-md)] border border-red-200">
                      <p className="font-semibold text-[var(--color-charcoal)] text-sm">{schwaeche.Name}</p>
                      {schwaeche.Beschreibung && schwaeche.Beschreibung !== '[placeholder]' && (
                        <p className="text-xs text-[var(--color-charcoal)]/70 mt-1">{schwaeche.Beschreibung}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vertriebsfokus (Sales Focus) */}
      {main.Produkt_Fit_Vertriebsfokus && main.Produkt_Fit_Vertriebsfokus !== '[placeholder]' && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <Crosshair className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.salesFocus')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="salesFocus" />
          </div>
          <div className="p-4 bg-gradient-to-r from-[var(--color-primary-start)]/5 to-[var(--color-primary-end)]/5 rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
              {main.Produkt_Fit_Vertriebsfokus}
            </p>
          </div>
        </div>
      )}

      {/* Summary section */}
      {main.Produkt_Zusammenfassung && main.Produkt_Zusammenfassung !== '[placeholder]' && (
        <div className="pt-4 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">{t('results.cards.produkt.overlay.summary')}</h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="summary" />
          </div>
          <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
            <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed">
              {main.Produkt_Zusammenfassung}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProduktOverlayContent;
